import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Heart, CheckCircle2, XCircle, MailCheck, LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function Shell({ children }) {
  return (
    <div className="min-h-screen grid place-items-center px-6" style={{ background: "#FDFBF7" }}>
      <div className="text-center max-w-md" data-testid="verify-page">
        <div className="flex items-center justify-center gap-2 mb-8"><Link to="/" data-testid="brand-home" className="flex items-center gap-2"><Heart size={18} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-2xl">Evermore</span></Link></div>
        {children}
      </div>
    </div>
  );
}

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [msg, setMsg] = useState("");
  const [redirect, setRedirect] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (!token) { setState("error"); setMsg("Missing verification token."); return; }
    if (started.current) return;
    started.current = true;
    api.post("/auth/verify-email", { token })
      .then(async ({ data }) => {
        const r = data?.redirect;
        setRedirect(r && typeof r === "string" && r.startsWith("/") && !r.startsWith("//") ? r : null);
        setState("ok");
        try { if (refreshUser) await refreshUser(); } catch (e) { /* ignore */ }
      })
      .catch((e) => { setState("error"); setMsg(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [token]); // eslint-disable-line

  return (
    <Shell>
      {state === "loading" && <p className="text-stone-400 font-serif text-2xl">Verifying…</p>}
      {state === "ok" && (
        <div data-testid="verify-ok">
          <CheckCircle2 size={44} className="text-[#8C9A86] mx-auto" />
          <h1 className="font-serif text-3xl mt-4">Email verified!</h1>
          <p className="text-stone-500 mt-2 text-sm">Your email is confirmed. You now have full access to Evermore.</p>
          <button onClick={async () => { if (refreshUser) await refreshUser(); navigate(redirect || "/app"); }} data-testid="verify-continue" className="mt-6 rounded-full bg-[#2C2A28] text-white px-7 py-3 text-sm hover:bg-[#8C9A86] transition-colors">{redirect ? "Continue to RSVP" : "Continue to Account Home Page"}</button>
        </div>
      )}
      {state === "error" && (
        <div data-testid="verify-error">
          <XCircle size={44} className="text-[#C67A7A] mx-auto" />
          <h1 className="font-serif text-3xl mt-4">Verification failed</h1>
          <p className="text-stone-500 mt-2 text-sm">{msg}</p>
          <Link to="/app" className="mt-6 inline-block text-sm text-[#8C9A86] hover:underline">Go to Evermore</Link>
        </div>
      )}
    </Shell>
  );
}

export function VerifyPending() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent. Check your inbox."); }
    catch { toast.error("Could not resend right now."); }
    finally { setBusy(false); }
  };
  const check = async () => {
    const u = refreshUser ? await refreshUser() : null;
    if (u?.email_verified) toast.success("You're verified. Welcome!");
    else toast.info("Not verified yet. Please click the link in your email.");
  };
  return (
    <Shell>
      <MailCheck size={44} className="text-[#8C9A86] mx-auto" />
      <h1 className="font-serif text-3xl mt-4">Verify your email</h1>
      <p className="text-stone-500 mt-2 text-sm">We've sent a verification link to <span className="font-medium text-stone-700">{user?.email}</span>. Please verify it to access your Evermore account.</p>
      <div className="mt-6 flex flex-col items-center gap-2">
        <button onClick={check} data-testid="verify-check" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-7 py-3 text-sm hover:bg-[#8C9A86] transition-colors"><RefreshCw size={15} /> I've verified my email</button>
        <button onClick={resend} disabled={busy} data-testid="verify-resend" className="text-sm text-[#8C9A86] hover:underline disabled:opacity-50">{busy ? "Sending…" : "Resend verification email"}</button>
        <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-1.5 text-sm text-stone-400 hover:text-[#C67A7A] mt-2"><LogOut size={14} /> Sign out</button>
      </div>
    </Shell>
  );
}
